// Rule-based intent parser to convert user input into a ChartSpec
// Uses AnalyticsDashboard data when available; falls back to local mock data.

import { AnalyticsResult } from "@dashboard/welcomePage/analytics/models/analytics";

import { ChartSpec, ChartType, inventoryByCategory, outOfStockByCategory, refundsByMonth, salesByMonth, visitsByDay } from "./mockData";

function inferType(text: string): ChartType {
  if (text.includes("line") || text.includes("over time")) return "line";

  if (text.includes("pie") || text.includes("share") || text.includes("distribution")) return "pie";

  return "bar";
}

export function parseIntentToChartSpec(input: string, analytics?: AnalyticsResult): ChartSpec | null {
  const text = input.toLowerCase();
  const type = inferType(text);

  // dataset selection
  const isInventory = text.includes("inventory") || text.includes("stock") || text.includes("out of stock");
  const isTraffic = text.includes("traffic") || text.includes("visits") || text.includes("visitors");
  const isSalesKeyword = text.includes("sales") || text.includes("revenue") || text.includes("orders");
  const isAov = text.includes("average order value") || text.includes("aov");
  const isBest = text.includes("best-selling") || text.includes("best sellers") || /\btop\b/.test(text);
  const isSales = isSalesKeyword || isAov || isBest;
  const isRefunds = text.includes("refund");
  const isQ4 = text.includes("q4") || text.includes("quarter 4") || text.includes("fourth quarter");

  if (!isInventory && !isTraffic && !isSales && !isRefunds) return null;

  if (isSales) {
    // Sales by channel
    if (text.includes("channel") && analytics && analytics.salesByChannel?.length) {
      const data = analytics.salesByChannel.map((c) => ({ label: `${c.channel} / ${c.currency}`, value: c.gmv }));

      return {
        title: "Sales by Channel",
        type: type === "line" ? "bar" : type, // prefer bar/pie for categorical breakdown
        data,
        xLabel: "Channel",
        yLabel: "GMV",
      };
    }

    // Sales by category
    if (text.includes("category") && analytics && analytics.salesByCategory?.length) {
      const data = analytics.salesByCategory.map((c) => ({ label: c.category, value: c.gmv }));

      return {
        title: "Sales by Category",
        type: type === "line" ? "bar" : type,
        data,
        xLabel: "Category",
        yLabel: "GMV",
      };
    }

    // Top best-selling products
    if ((text.includes("best-selling") || text.includes("best sellers") || text.includes("top")) && analytics && analytics.bestSellers?.length) {
      // Default Top 5 by units
      const topN = 5;
      const data = analytics.bestSellers
        .slice(0, topN)
        .map(p => ({ label: p.name, value: p.units }));

      return {
        title: `Top ${topN} Best-selling Products (Units)`,
        type: "bar",
        data,
        xLabel: "Product",
        yLabel: "Units",
      };
    }

    // Average order value
    if (text.includes("average order value") || text.includes("aov")) {
      const value = analytics?.avgOrderValue ?? 0;

      return {
        title: "Average Order Value",
        type: "bar",
        data: [{ label: "AOV", value }],
        xLabel: "",
        yLabel: "Amount",
      };
    }

    // Prefer live analytics series if available
    if (analytics && analytics.salesSeries?.length) {
      const data = analytics.salesSeries.map((p) => ({ label: p.date.substring(5), value: p.value }));

      return {
        title: "Sales over Time",
        type,
        data,
        xLabel: "Date",
        yLabel: "GMV",
      };
    }

    // Special handling for Q4 requests: subset Oct-Dec
    if (isQ4) {
      const q4 = salesByMonth.filter(d => ["Oct", "Nov", "Dec"].includes(d.label));

      return {
        title: "Projected Revenue — Q4",
        type,
        data: q4.length ? q4 : salesByMonth,
        xLabel: "Month",
        yLabel: "Revenue",
      };
    }

    return {
      title: "Sales by Month",
      type,
      data: salesByMonth,
      xLabel: "Month",
      yLabel: "Revenue",
    };
  }

  if (isRefunds) {
    // If analytics provided, estimate refunds as refundRate * GMV per month
    if (analytics && analytics.salesSeries?.length) {
      // Explicit requests
      if (text.includes("rate")) {
        const rate = +(analytics.refundRate ?? 0) * 100;

        return {
          title: "Refund Rate",
          type: "bar",
          data: [{ label: "Refund Rate", value: +rate.toFixed(2) }],
          xLabel: "",
          yLabel: "Rate (%)",
        };
      }

      if (text.includes("compare") || text.includes("cancellation")) {
        const r = +((analytics.refundRate ?? 0) * 100).toFixed(2);
        const c = +((analytics.cancellationsRate ?? 0) * 100).toFixed(2);

        return {
          title: "Refunds vs Cancellations (Rates)",
          type: "bar",
          data: [
            { label: "Refund Rate", value: r },
            { label: "Cancellation Rate", value: c },
          ],
          xLabel: "",
          yLabel: "Rate (%)",
        };
      }

      // Daily refunds trend based on current date range
      if (text.includes("trend") || text.includes("last 30")) {
        const daily = analytics.salesSeries.map(p => ({ label: p.date.substring(5), value: +(p.value * (analytics.refundRate ?? 0)).toFixed(2) }));

        return {
          title: "Refunds Trend",
          type: "line",
          data: daily,
          xLabel: "Date",
          yLabel: "Refunds (est.)",
        };
      }

      const monthMap = new Map<string, number>();

      for (const p of analytics.salesSeries) {
        const d = new Date(p.date + "T00:00:00Z");
        const month = d.toLocaleString("en-US", { month: "short" });

        monthMap.set(month, (monthMap.get(month) ?? 0) + p.value * (analytics.refundRate ?? 0));
      }

      const monthOrder = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const data = monthOrder
        .filter(m => monthMap.has(m))
        .map(m => ({ label: m, value: +((monthMap.get(m) ?? 0)).toFixed(2) }));

      return {
        title: "Estimated Refunds by Month",
        type,
        data: data.length ? data : refundsByMonth,
        xLabel: "Month",
        yLabel: "Refunds (est.)",
      };
    }

    // Fallback to local mock
    return {
      title: "Refunds by Month",
      type,
      data: refundsByMonth,
      xLabel: "Month",
      yLabel: "Refunds",
    };
  }

  if (isInventory) {
    // If Analytics data present, show low stock items instead of generic category inventory
    if (analytics && analytics.lowStock?.length) {
      // explicit out-of-stock or threshold requests handled below
      if (text.includes("top") || text.includes("low-stock") || text.includes("low stock")) {
        const data = analytics.lowStock
          .slice()
          .sort((a, b) => a.available - b.available)
          .slice(0, 10)
          .map(i => ({ label: i.variant, value: i.available }));

        return {
          title: "Top 10 Low-stock Variants",
          type: "bar",
          data,
          xLabel: "Variant",
          yLabel: "Available",
        };
      }

      if (text.includes("out-of-stock") || text.includes("out of stock")) {
        return {
          title: "Out-of-stock SKUs",
          type: "bar",
          data: [{ label: "Out of Stock", value: analytics.outOfStockSkus ?? 0 }],
          xLabel: "",
          yLabel: "Count",
        };
      }

      if (text.includes("threshold")) {
        return {
          title: "Low Stock Threshold",
          type: "bar",
          data: [{ label: "Threshold", value: analytics.lowStockThreshold ?? 0 }],
          xLabel: "",
          yLabel: "Units",
        };
      }

      const data = analytics.lowStock
        .slice()
        .sort((a, b) => a.available - b.available)
        .slice(0, 10)
        .map(i => ({ label: i.variant, value: i.available }));

      return {
        title: "Low Stock by Variant",
        type: type === "pie" ? "bar" : type, // pie isn't great for many items
        data,
        xLabel: "Variant",
        yLabel: "Available",
      };
    }

    // If the phrase includes out of stock, prefer the out-of-stock dataset and default to pie for share
    if (text.includes("out of stock")) {
      const preferredType: ChartType = text.includes("bar") || text.includes("line") || text.includes("pie") ? type : "pie";

      return {
        title: "Out of Stock by Category",
        type: preferredType,
        data: outOfStockByCategory,
        xLabel: "Category",
        yLabel: "Count",
      };
    }

    return {
      title: "Inventory by Category",
      type,
      data: inventoryByCategory,
      xLabel: "Category",
      yLabel: "Qty",
    };
  }

  // Orders by status (if asked as generic without keywords above)
  if (text.includes("orders") && text.includes("status") && analytics?.statusBreakdown?.length) {
    const data = analytics.statusBreakdown.map(s => ({ label: s.status, value: s.count }));

    return {
      title: "Orders by Status",
      type: "bar",
      data,
      xLabel: "Status",
      yLabel: "Count",
    };
  }

  return {
    title: "Traffic by Day",
    type,
    data: visitsByDay,
    xLabel: "Day",
    yLabel: "Visits",
  };
}
